import { useEffect } from "preact/hooks";
import { startLessonById } from "./actions.ts";
import { CreditsDialog } from "./CreditsDialog.tsx";
import { Home } from "./pages/Home.tsx";
import { Landing } from "./pages/Landing.tsx";
import { Playground } from "./pages/Playground.tsx";
import { Policies } from "./pages/Policies.tsx";
import { SignIn } from "./pages/SignIn.tsx";
import { Usage } from "./pages/Usage.tsx";
import { Welcome } from "./pages/Welcome.tsx";
import { isPublic, navigate, useRoute, type Route } from "./router.ts";
import { Shell } from "./Shell.tsx";
import { useStore } from "./store.ts";

// The signed-in page for a route. A route that has no page here, such as
// "landing", goes home. A new route fails to compile until it is listed.
const Page = ({ route }: { route: Route }) => {
  switch (route.name) {
    case "playground": return <Playground />;
    case "usage": return <Usage />;
    case "home":
    case "landing":
    case "sign-in":
    case "welcome":
    case "policies":
    case "not-found": return <Home />;
    default: return route satisfies never;
  }
};

export function App() {
  const route = useRoute();
  const signedIn = useStore((s) => s.signedIn);
  const creditsOpen = useStore((s) => s.creditsOpen);
  const lessonId = route.name === "playground" ? route.lesson : null;
  // The lessons are built after sign-in, so a lesson's link can open before they exist.
  const lessonsReady = useStore((s) => s.content.lessons.length > 0);

  // Signed-out visitors see only the homepage, sign-in and policies.
  useEffect(() => {
    if (!signedIn && !isPublic(route)) navigate("");
    else if (signedIn && (route.name === "landing" || route.name === "sign-in")) navigate("home");
  }, [signedIn, route.name]);

  // Opening a lesson's link starts the lesson; the bare Playground keeps what's there.
  useEffect(() => { if (route.name === "playground" && lessonId) startLessonById(lessonId); }, [lessonId, lessonsReady]);

  if (!signedIn) {
    if (route.name === "sign-in") return <SignIn />;
    if (route.name === "policies") return <Policies />;
    return <Landing />;
  }
  if (route.name === "welcome") return <Welcome />;
  if (route.name === "policies") return <Policies />;
  return (
    <Shell route={route}>
      <Page route={route} />
      {creditsOpen && <CreditsDialog />}
    </Shell>
  );
}
